import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "transactions-view" };
const _hoisted_2 = { class: "transactions-header" };
const _hoisted_3 = { class: "content-placeholder" };
const _hoisted_4 = { key: 3 };
const _hoisted_5 = {
    key: 1,
    class: "flex items-center justify-center gap-2 mb-4",
    "aria-live": "polite"
};
const _hoisted_6 = { class: "text-gray-600" };
const _hoisted_7 = {
    key: 2,
    class: "mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700",
    role: "alert"
};
const _hoisted_8 = { class: "flex items-start gap-2" };
const _hoisted_9 = { class: "font-medium" };
const _hoisted_10 = { key: 0 };
import { ref, nextTick } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useI18n } from "vue-i18n";
import { ExportService } from "@/infrastructure/services/ExportService";
import { useToast } from "@/shared/composables/useToast";
import ExportDialog from "./ExportDialog.vue";
import "@adyen/adyen-platform-experience-web/adyen-platform-experience-web.css";
import { onMounted, onUnmounted } from "vue";
import { TransactionUseCases } from "@/application/use-cases/TransactionUseCases";
import { useLogger } from "@/shared/composables/useLogger";
import { useLocaleChange } from "@/shared/composables/useLocaleChange";
import { useAccountInfo } from "@/shared/composables/useAccountInfo";
import DashboardLoadingState from "@/presentation/components/dashboard/DashboardLoadingState.vue";
import DashboardErrorState from "@/presentation/components/dashboard/DashboardErrorState.vue";
import DashboardOnboardingIncomplete from "@/presentation/components/dashboard/DashboardOnboardingIncomplete.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Transactions',
    setup(__props) {
        const logger = useLogger();
        const { isAccountActive, hasAccountInfo, error: accountError, isLoading: isLoadingAccount, fetchAccountInfo, } = useAccountInfo();
        const transactionUseCases = new TransactionUseCases();
        const route = useRoute();
        const router = useRouter();
        const isLoadingDashboard = ref(true);
        const isLoadingAdyenComponent = ref(false);
        const isSearching = ref(false);
        const searchError = ref(null);
        const currentTransactionId = ref(null);
        const isDetailsModalOpen = ref(false);
        /**
         * Initialize or reinitialize the dashboard
         * Fetches account info and initializes Adyen components if account is active
         */
        async function initializeDashboard() {
            try {
                // Fetch account info to check status
                await fetchAccountInfo();
            }
            catch (error) {
                logger.debug("Failed to fetch account info:", error);
            }
            finally {
                // Stop initial loading to reveal the appropriate view
                isLoadingDashboard.value = false;
            }
            // Only initialize dashboard if account is active
            if (isAccountActive.value) {
                // Wait for Vue to render the container before attempting to mount Adyen component
                await nextTick();
                isLoadingAdyenComponent.value = true;
                try {
                    // Initialize the transaction dashboard (container now exists in DOM)
                    await transactionUseCases.initializeTransactionDashboard("#transactions-overview-container", async (adyenTransactionId) => {
                        if (!adyenTransactionId)
                            return;
                        // Navigate using router as single source of truth
                        await router.push({
                            path: "/dashboard/transactions",
                            query: { transactionId: adyenTransactionId },
                        });
                    });
                    // Wait for Adyen component to render content before hiding loader
                    await waitForAdyenContent("#transactions-overview-container");
                    // On load: if URL has adyenTransactionId in paymentReference, open details directly
                    const adyenTransactionId = route.query.transactionId;
                    if (adyenTransactionId) {
                        await openDetails(adyenTransactionId);
                    }
                }
                catch (error) {
                    logger.debug("Failed to initialize transaction dashboard:", error);
                    // Error is already handled by the use case with proper navigation
                }
                finally {
                    // Hide Adyen component loader after everything is loaded (or failed)
                    isLoadingAdyenComponent.value = false;
                }
            }
        }
        /**
         * Retry initialization when the user clicks the retry button
         * Resets the loading state and reinitializes the entire dashboard
         */
        async function retryInitialization() {
            // Reset loading state to show the loader
            isLoadingDashboard.value = true;
            // Cleanup any existing Adyen components before retrying
            transactionUseCases.cleanup();
            // Wait for Vue to clean up the DOM
            await nextTick();
            // Reinitialize the dashboard
            await initializeDashboard();
        }
        onMounted(async () => {
            await initializeDashboard();
        });
        /**
         * Wait for Adyen component content to be rendered in the container
         * Uses MutationObserver with RAF throttling to detect when content is added
         * Optimized for performance: throttles callbacks, only checks direct children
         */
        function waitForAdyenContent(selector, timeout = 5000) {
            return new Promise((resolve) => {
                const container = document.querySelector(selector);
                if (!container) {
                    resolve(); // Container not found, resolve to avoid blocking
                    return;
                }
                // Check if content already exists
                if (container.children.length > 0) {
                    // Wait for next frame to ensure rendering is complete
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            resolve();
                        });
                    });
                    return;
                }
                let isResolved = false;
                let rafId = null;
                const checkContent = () => {
                    if (isResolved)
                        return;
                    // Only check direct children length (fast check, no DOM traversal)
                    // This works even if Adyen creates a wrapper div
                    if (container.children.length > 0) {
                        isResolved = true;
                        if (rafId !== null) {
                            cancelAnimationFrame(rafId);
                        }
                        observer.disconnect();
                        clearTimeout(timeoutId);
                        // Wait for next frame to ensure rendering is complete
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                resolve();
                            });
                        });
                    }
                };
                // Throttle observer callbacks using requestAnimationFrame
                // This ensures we only check once per frame max, even if many mutations occur
                const observer = new MutationObserver(() => {
                    if (isResolved)
                        return;
                    // Cancel previous RAF if pending (debouncing)
                    if (rafId !== null) {
                        cancelAnimationFrame(rafId);
                    }
                    // Schedule check on next frame (throttling to ~60fps max)
                    rafId = requestAnimationFrame(checkContent);
                });
                const timeoutId = setTimeout(() => {
                    if (!isResolved) {
                        isResolved = true;
                        if (rafId !== null) {
                            cancelAnimationFrame(rafId);
                        }
                        observer.disconnect();
                        resolve(); // Resolve anyway to avoid blocking UI
                    }
                }, timeout);
                // Observe subtree to catch Adyen wrapper divs, but throttle callbacks aggressively
                // Only childList (no characterData) to avoid excessive callbacks
                observer.observe(container, {
                    childList: true,
                    subtree: true, // Needed if Adyen creates wrapper divs
                    // No characterData: true - avoids excessive callbacks on text changes
                });
            });
        }
        // Listen to locale changes and update Adyen component locale
        useLocaleChange(async (locale) => {
            logger.debug(`Locale changed to ${locale}, updating Adyen component...`);
            try {
                await transactionUseCases.updateLocale(locale);
                logger.debug("Adyen component locale updated successfully");
            }
            catch (error) {
                logger.debug("Failed to update Adyen component locale:", error);
            }
        });
        // React to router changes: open/close details when query param changes
        onBeforeRouteUpdate((to) => {
            const id = to.query.transactionId;
            if (id) {
                openDetails(id);
            }
            else if (isDetailsModalOpen.value) {
                closeDetailsModal();
            }
        });
        async function openDetails(adyenTransactionId) {
            isSearching.value = true;
            searchError.value = null;
            try {
                currentTransactionId.value = adyenTransactionId;
                isDetailsModalOpen.value = true;
                await nextTick();
                await transactionUseCases.initializeTransactionDetails("#transaction-details-container", adyenTransactionId);
            }
            catch (err) {
                searchError.value = err instanceof Error ? err.message : "Open failed";
                isDetailsModalOpen.value = false;
            }
            finally {
                isSearching.value = false;
            }
        }
        async function closeDetailsModal() {
            isDetailsModalOpen.value = false;
            const container = document.querySelector("#transaction-details-container");
            if (container) {
                container.innerHTML = "";
            }
            currentTransactionId.value = null;
            // Clean URL: remove query when closing
            await router.replace({ query: { ...route.query, transactionId: undefined } }).catch(() => { });
        }
        onUnmounted(() => {
            transactionUseCases.cleanup();
        });
        const { t } = useI18n();
        const { showSuccess, showError } = useToast();
        const isExportModalOpen = ref(false);
        const isSubmitting = ref(false);
        function openExportModal() {
            isExportModalOpen.value = true;
        }
        function closeExportModal() {
            isExportModalOpen.value = false;
        }
        async function handleExportSubmit(payload) {
            isSubmitting.value = true;
            try {
                // Call backend export endpoint
                const ok = await ExportService.exportReport({
                    type: payload.type,
                    startDate: payload.startDate,
                    endDate: payload.endDate,
                });
                if (ok) {
                    showSuccess(t("dashboard.exportDialog.success"));
                    closeExportModal();
                }
                else {
                    showError(t("dashboard.exportDialog.error"));
                }
            }
            catch (error) {
                logger.debug("Failed to export report", error);
                showError(t("dashboard.exportDialog.error"));
            }
            finally {
                isSubmitting.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", null, _toDisplayString(_unref(t)("dashboard.menu.transactions")), 1 /* TEXT */),
                    (_unref(isAccountActive))
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            "data-testid": "export-button",
                            class: "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
                            onClick: openExportModal
                        }, [
                            _cache[1] || (_cache[1] = _createElementVNode("svg", {
                                class: "w-4 h-4",
                                "aria-hidden": "true",
                                xmlns: "http://www.w3.org/2000/svg",
                                fill: "none",
                                viewBox: "0 0 24 24"
                            }, [
                                _createElementVNode("path", {
                                    stroke: "currentColor",
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    "stroke-width": "2",
                                    d: "M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
                                })
                            ], -1 /* CACHED */)),
                            _createElementVNode("span", null, _toDisplayString(_unref(t)("dashboard.export")), 1 /* TEXT */)
                        ]))
                        : _createCommentVNode("v-if", true)
                ]),
                _createElementVNode("div", _hoisted_3, [
                    (isLoadingDashboard.value || _unref(isLoadingAccount))
                        ? (_openBlock(), _createBlock(DashboardLoadingState, {
                            key: 0,
                            overlay: ""
                        }))
                        : (_unref(accountError) !== null)
                            ? (_openBlock(), _createBlock(DashboardErrorState, {
                                key: 1,
                                title: _unref(t)('dashboard.loadAccountInfoError.title'),
                                message: _unref(t)('dashboard.loadAccountInfoError.message'),
                                "retry-label": _unref(t)('dashboard.loadAccountInfoError.retry'),
                                onRetry: retryInitialization
                            }, null, 8 /* PROPS */, ["title", "message", "retry-label"]))
                            : (_unref(hasAccountInfo) && !_unref(isAccountActive))
                                ? (_openBlock(), _createBlock(DashboardOnboardingIncomplete, {
                                    key: 2,
                                    title: _unref(t)('dashboard.onboardingIncomplete.title'),
                                    message: _unref(t)('dashboard.onboardingIncomplete.message'),
                                    "action-label": _unref(t)('dashboard.onboardingIncomplete.action'),
                                    onAction: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/onboarding')))
                                }, null, 8 /* PROPS */, ["title", "message", "action-label"]))
                                : (_unref(isAccountActive))
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        (isLoadingAdyenComponent.value)
                                            ? (_openBlock(), _createBlock(DashboardLoadingState, {
                                                key: 0,
                                                overlay: ""
                                            }))
                                            : _createCommentVNode("v-if", true),
                                        (isSearching.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                                _cache[2] || (_cache[2] = _createElementVNode("svg", {
                                                    class: "animate-spin w-5 h-5 text-blue-600",
                                                    xmlns: "http://www.w3.org/2000/svg",
                                                    viewBox: "0 0 24 24",
                                                    fill: "none",
                                                    "aria-hidden": "true"
                                                }, [
                                                    _createElementVNode("circle", {
                                                        class: "opacity-25",
                                                        cx: "12",
                                                        cy: "12",
                                                        r: "10",
                                                        stroke: "currentColor",
                                                        "stroke-width": "4"
                                                    }),
                                                    _createElementVNode("path", {
                                                        class: "opacity-75",
                                                        fill: "currentColor",
                                                        d: "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    })
                                                ], -1 /* CACHED */)),
                                                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)("dashboard.transactions.searching")), 1 /* TEXT */)
                                            ]))
                                            : _createCommentVNode("v-if", true),
                                        (searchError.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                                _createElementVNode("div", _hoisted_8, [
                                                    _cache[3] || (_cache[3] = _createElementVNode("svg", {
                                                        class: "w-4 h-4 mt-0.5 text-red-600",
                                                        xmlns: "http://www.w3.org/2000/svg",
                                                        viewBox: "0 0 20 20",
                                                        fill: "currentColor",
                                                        "aria-hidden": "true"
                                                    }, [
                                                        _createElementVNode("path", {
                                                            "fill-rule": "evenodd",
                                                            d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-4a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 8a1 1 0 100-2 1 1 0 000 2z",
                                                            "clip-rule": "evenodd"
                                                        })
                                                    ], -1 /* CACHED */)),
                                                    _createElementVNode("div", null, [
                                                        _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)("dashboard.transactions.searchError")), 1 /* TEXT */)
                                                    ])
                                                ])
                                            ]))
                                            : _createCommentVNode("v-if", true),
                                        _cache[4] || (_cache[4] = _createElementVNode("div", { id: "transactions-overview-container" }, null, -1 /* CACHED */))
                                    ]))
                                    : _createCommentVNode("v-if", true)
                ]),
                (isDetailsModalOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/70" }, null, -1 /* CACHED */)),
                        _createElementVNode("div", { class: "fixed inset-0 z-50 flex items-center justify-center p-4" }, [
                            _createElementVNode("div", { class: "relative w-full max-w-3xl bg-transparent shadow-none rounded-none" }, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 text-gray-400 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:text-gray-300",
                                    "aria-label": "Close",
                                    onClick: closeDetailsModal
                                }, [...(_cache[5] || (_cache[5] = [
                                        _createElementVNode("span", { class: "sr-only" }, "Close", -1 /* CACHED */),
                                        _createElementVNode("svg", {
                                            class: "w-4 h-4",
                                            xmlns: "http://www.w3.org/2000/svg",
                                            fill: "none",
                                            viewBox: "0 0 14 14",
                                            "aria-hidden": "true"
                                        }, [
                                            _createElementVNode("path", {
                                                stroke: "currentColor",
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "stroke-width": "2",
                                                d: "M1 1l12 12M13 1L1 13"
                                            })
                                        ], -1 /* CACHED */)
                                    ]))]),
                                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "p-0" }, [
                                    _createElementVNode("div", { id: "transaction-details-container" })
                                ], -1 /* CACHED */))
                            ])
                        ])
                    ]))
                    : _createCommentVNode("v-if", true),
                (isExportModalOpen.value)
                    ? (_openBlock(), _createBlock(ExportDialog, {
                        key: 1,
                        open: isExportModalOpen.value,
                        loading: isSubmitting.value,
                        onClose: closeExportModal,
                        onSubmit: handleExportSubmit
                    }, null, 8 /* PROPS */, ["open", "loading"]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
